import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, ChevronDown, Download, FolderDown, Loader2, Monitor, Play, RefreshCw, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

/**
 * Windows 메신저(HubCore 메신저) 설치 섹션 — 다운로드 카드 + 설치 방법 + 설치가 막힐 때.
 *
 * `/desktop-app` 페이지와 관리자 강제 설치 안내 모달(DesktopAppForceDialog)이 함께 쓴다.
 * 설치 파일과 버전 정보는 이 서버의 /desktop/ 경로(nginx 정적, desktop/releases)에서 읽는다.
 * latest.yml 은 데스크톱 앱 배포(publish-release.sh) 때마다 갱신되므로 항상 최신 버전을 가리킨다.
 * 같은 오리진을 쓰므로 사내(i1)·외부(ione) 어느 주소로 들어와도 그 주소에서 내려받는다.
 */
export interface LatestInfo {
  version: string;
  fileName: string;
  size: number | null;
  releaseDate: string | null;
}

export async function fetchLatest(): Promise<LatestInfo> {
  const res = await fetch(`/desktop/latest.yml?t=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`latest.yml ${res.status}`);
  const text = await res.text();
  const version = /^version:\s*(.+)$/m.exec(text)?.[1]?.trim();
  const fileName = /^\s*-\s*url:\s*(.+)$/m.exec(text)?.[1]?.trim();
  if (!version || !fileName) throw new Error("latest.yml 형식을 읽을 수 없습니다");
  const size = /^\s*size:\s*(\d+)/m.exec(text)?.[1];
  const releaseDate = /^releaseDate:\s*'?([^'\n]+)'?/m.exec(text)?.[1]?.trim();
  return { version, fileName, size: size ? Number(size) : null, releaseDate: releaseDate ?? null };
}

export function useDesktopAppLatest(enabled = true) {
  return useQuery({ queryKey: ["desktop-app-latest"], queryFn: fetchLatest, staleTime: 60_000, retry: 1, enabled });
}

export function formatSize(bytes: number | null): string {
  if (!bytes) return "";
  return `${(bytes / 1024 / 1024).toFixed(1)} MB (${bytes.toLocaleString()} 바이트)`;
}

export function formatDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric" });
}

/**
 * 설치 파일을 페이지가 직접 스트리밍으로 받아 진행률을 보여 준 뒤 브라우저 저장으로 넘긴다 (대표 요청 2026-10-23).
 * 브라우저는 받은 .exe 를 실행할 수 없으므로 "실행" 은 2단계 안내(다운로드 목록에서 파일 클릭)로 유도한다.
 * fetch 가 실패하면(프록시·메모리 등) 일반 링크 다운로드로 폴백한다.
 */
type DownloadState =
  | { phase: "idle" }
  | { phase: "downloading"; received: number; total: number | null }
  | { phase: "done"; bytes: number }
  | { phase: "error"; message: string };

async function streamDownload(
  url: string,
  fileName: string,
  expectedSize: number | null,
  onProgress: (received: number, total: number | null) => void,
  signal: AbortSignal,
): Promise<number> {
  const res = await fetch(url, { cache: "no-store", signal });
  if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
  const headerLen = Number(res.headers.get("content-length"));
  const total = Number.isFinite(headerLen) && headerLen > 0 ? headerLen : expectedSize;
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    onProgress(received, total);
  }
  const blob = new Blob(chunks as BlobPart[], { type: "application/octet-stream" });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 60_000);
  return received;
}

function formatMb(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

interface WindowsInstallSectionProps {
  /** 다운로드 카드 강조 테두리 (페이지에서 Windows 가 주 대상일 때) */
  highlight?: boolean;
  /** 모달용 — "설치가 막힐 때" 를 접어 두고 카드 제목을 작게 */
  compact?: boolean;
}

export default function WindowsInstallSection({ highlight = true, compact = false }: WindowsInstallSectionProps) {
  const latest = useDesktopAppLatest();
  const downloadHref = latest.data ? `/desktop/${latest.data.fileName}` : undefined;
  const [dl, setDl] = useState<DownloadState>({ phase: "idle" });
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);

  const startDownload = async () => {
    if (!latest.data || !downloadHref) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setDl({ phase: "downloading", received: 0, total: latest.data.size });
    try {
      const bytes = await streamDownload(
        downloadHref,
        latest.data.fileName,
        latest.data.size,
        (received, total) => setDl({ phase: "downloading", received, total }),
        controller.signal,
      );
      setDl({ phase: "done", bytes });
    } catch (e) {
      if (controller.signal.aborted) return;
      setDl({ phase: "error", message: e instanceof Error ? e.message : String(e) });
    }
  };
  const sizeMismatch = dl.phase === "done" && !!latest.data?.size && dl.bytes !== latest.data.size;
  const percent =
    dl.phase === "downloading" && dl.total ? Math.min(100, Math.round((dl.received / dl.total) * 100)) : null;

  const blockedContent = (
    <dl className="space-y-4 text-sm">
      <div>
        <dt className="font-medium">"Microsoft Store의 확인된 앱이 아닙니다" 안내가 뜨거나, 실행해도 아무 반응이 없을 때</dt>
        <dd className="mt-1 text-muted-foreground">
          Windows 설정 → 앱 → 고급 앱 설정 → <span className="font-medium text-foreground">앱을 가져올 위치</span>를
          "어디서나"로 바꾼 뒤 설치 파일을 다시 실행합니다. (Windows 10은 설정 → 앱 → 앱 및 기능에 같은 항목이 있습니다.)
          이 항목이 잠겨 있고 "S 모드"라고 표시되면 설정 → 시스템 → 정품 인증에서 S 모드를 해제해야 합니다.
        </dd>
      </div>
      <div>
        <dt className="font-medium">"PC 보호" 파란 창에 "실행" 버튼이 보이지 않을 때</dt>
        <dd className="mt-1 text-muted-foreground">
          받은 설치 파일을 마우스 오른쪽 버튼 → 속성 → 일반 탭 맨 아래
          <span className="font-medium text-foreground"> "차단 해제"</span>에 체크하고 확인을 누른 뒤 다시 실행합니다.
          인터넷에서 받은 파일에 붙는 표시를 지우는 것으로, 파일 자체는 바뀌지 않습니다.
        </dd>
      </div>
      <div>
        <dt className="font-medium">백신(보안 프로그램)이 설치 파일을 지우거나 격리할 때</dt>
        <dd className="mt-1 text-muted-foreground">
          격리함에서 파일을 복원하고 "신뢰(예외)"에 등록한 뒤 실행합니다. 설치 후에는 메신저가 설치된 폴더
          <code className="mx-1 rounded bg-muted px-1 py-0.5 text-xs">%LocalAppData%\Programs\HubCore Messenger</code>
          를 예외에 추가해 두면 자동 업데이트도 막히지 않습니다. 예외 등록이 안 되면 실시간 검사를 잠시 끄고 설치한 뒤 다시 켭니다.
        </dd>
      </div>
      <div>
        <dt className="font-medium">"시스템 관리자가 이 앱 실행을 차단했습니다"라고 나올 때</dt>
        <dd className="mt-1 text-muted-foreground">
          회사 정책(AppLocker 등)으로 관리되는 PC라 사용자가 직접 풀 수 없습니다. IT 담당자에게 위 설치 파일 이름과 설치 폴더를
          알려 주고 허용 목록에 넣어 달라고 요청하세요.
        </dd>
      </div>
    </dl>
  );

  return (
    <>
      <Card className={highlight ? "border-primary/20" : undefined}>
        <CardHeader className="pb-3">
          <CardTitle className={`${compact ? "text-base" : "text-lg"} flex items-center gap-2`}>
            <Monitor className="h-5 w-5 text-primary" />
            1단계: Windows 메신저 다운로드
          </CardTitle>
          <CardDescription>새 버전을 배포하면 이 버튼이 자동으로 최신 버전을 가리킵니다. 진행률이 끝나면 브라우저가 파일을 저장합니다.</CardDescription>
        </CardHeader>
        <CardContent>
          {latest.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-10 w-48" />
            </div>
          ) : latest.isError || !latest.data ? (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-destructive">설치 파일 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.</p>
              <Button variant="outline" size="sm" onClick={() => latest.refetch()}>
                <RefreshCw className="h-4 w-4 mr-1" /> 다시 시도
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-base font-semibold">HubCore 메신저</span>
                  <Badge variant="secondary">v{latest.data.version}</Badge>
                  <Badge variant="outline">Windows 10 / 11</Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {latest.data.fileName}
                  {latest.data.size ? ` · ${formatSize(latest.data.size)}` : ""}
                  {latest.data.releaseDate ? ` · ${formatDate(latest.data.releaseDate)} 배포` : ""}
                </p>
              </div>
              {dl.phase === "downloading" ? (
                <div className="w-full sm:w-64 space-y-1.5">
                  <Progress value={percent ?? 0} className="h-2" />
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {dl.total ? `${formatMb(dl.received)} / ${formatMb(dl.total)} · ${percent}%` : `${formatMb(dl.received)} 받는 중`}
                  </p>
                </div>
              ) : dl.phase === "done" ? (
                <div className="flex flex-col items-start gap-1 sm:items-end">
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 dark:text-emerald-300">
                    <CheckCircle2 className="h-4 w-4" /> 다운로드 완료 · 아래 2단계로
                  </span>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={startDownload}>
                    <RefreshCw className="h-3 w-3 mr-1" /> 다시 받기
                  </Button>
                </div>
              ) : (
                <Button size="lg" className="shrink-0" onClick={startDownload}>
                  <Download className="h-4 w-4 mr-2" /> {dl.phase === "error" ? "다시 시도" : "다운로드"}
                </Button>
              )}
            </div>
          )}
          {dl.phase === "error" && downloadHref && latest.data && (
            <p className="mt-3 text-xs text-amber-700 dark:text-amber-300">
              페이지 안 다운로드가 실패했습니다({dl.message}).{" "}
              <a href={downloadHref} download={latest.data.fileName} className="underline font-medium">
                브라우저로 직접 받기
              </a>
            </p>
          )}
          {sizeMismatch && latest.data?.size && dl.phase === "done" && (
            <p className="mt-3 text-xs text-amber-700 dark:text-amber-300">
              받은 크기({dl.bytes.toLocaleString()} 바이트)가 안내 크기({latest.data.size.toLocaleString()} 바이트)와 다릅니다. 다운로드가 끊겼을 수 있으니 "다시 받기"를 눌러 주세요.
            </p>
          )}
        </CardContent>
      </Card>

      <Card className={dl.phase === "done" ? "border-primary ring-1 ring-primary/30" : undefined}>
        <CardHeader className="pb-3">
          <CardTitle className={`${compact ? "text-base" : "text-lg"} flex items-center gap-2`}>
            <Play className="h-5 w-5 text-primary" />
            2단계: 받은 파일 실행 → 자동 설치
            {dl.phase === "done" && <Badge className="ml-1">다음 단계</Badge>}
          </CardTitle>
          <CardDescription>
            브라우저는 받은 설치 파일을 대신 실행해 주지 못합니다. 파일을 한 번 클릭하면 나머지 설치는 자동으로 진행됩니다.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal space-y-2 pl-5 text-sm">
            <li className="flex items-start gap-2 list-none -ml-5">
              <FolderDown className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span>
                브라우저 오른쪽 위 <span className="font-medium">다운로드 아이콘</span>(또는 <kbd className="rounded border bg-muted px-1 text-xs">Ctrl</kbd>+<kbd className="rounded border bg-muted px-1 text-xs">J</kbd>)을 열어{" "}
                <span className="font-medium">{latest.data?.fileName ?? "HubCoreMessenger-Setup.exe"}</span> 를 클릭합니다.
              </span>
            </li>
            <li>
              Windows가 "PC 보호" 경고를 띄우면 <span className="font-medium">추가 정보 → 실행</span>을 누릅니다. 설치 파일에 아직 코드
              서명이 없어 나오는 안내입니다.
            </li>
            <li>설치는 내 계정 폴더에 자동으로 진행되고 관리자 권한이 필요 없습니다. 이전 버전이 실행 중이면 설치 프로그램이 닫고 새 버전으로 바꿉니다. 끝나면 메신저가 자동으로 실행되니 웹과 같은 아이디와 비밀번호로 로그인하세요.</li>
            <li>이후 새 버전은 메신저가 시작할 때와 30분마다 확인해 자동으로 내려받고, 다음 실행 때 설치됩니다. 다시 설치할 필요가 없습니다.</li>
          </ol>
          <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              "Installer integrity check has failed" 오류가 나면 다운로드가 끊긴 것입니다. 받은 파일 크기가 위에 적힌 바이트 수와 같은지
              확인하고, 다르면 다시 내려받으세요. 크기가 같은데도 반복되면 백신의 실시간 검사를 잠시 끄고 실행해 보세요.
            </p>
          </div>
        </CardContent>
      </Card>

      {compact ? (
        <Collapsible>
          <Card>
            <CollapsibleTrigger asChild>
              <button type="button" className="group w-full text-left">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ShieldAlert className="h-5 w-5 text-amber-600" />
                    설치가 막힐 때
                    <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                  </CardTitle>
                  <CardDescription>보안 설정이 엄격한 PC에서 설치가 막히면 펼쳐서 확인하세요.</CardDescription>
                </CardHeader>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>{blockedContent}</CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-600" />
              설치가 막힐 때
            </CardTitle>
            <CardDescription>
              설치 파일에 아직 코드 서명이 없어 보안 설정이 엄격한 PC에서는 설치가 막힐 수 있습니다. 증상에 맞는 방법으로 풀고 다시 실행하세요.
              설치는 내 계정 폴더에만 되므로 관리자 권한은 필요 없습니다.
            </CardDescription>
          </CardHeader>
          <CardContent>{blockedContent}</CardContent>
        </Card>
      )}
    </>
  );
}
